// frontend/src/hooks/chat/useAgentV4Chat.ts
/**
 * Hook for handling Agent V4 chat with hybrid deterministic-AI architecture.
 *
 * Agent V4 features:
 * - Fast intent classification
 * - Deterministic precondition validation
 * - Research capabilities for complex queries
 * - Streaming responses
 * - Action confirmation flow
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { typedApi } from '../../services/typedApiClient';
import { API_URL } from '../../utils/constants';
import {
  createUserMessage,
  getAuthToken,
  createStreamingAssistantMessage,
  type ChatMessage,
  type ChatAction,
  type ChatMetrics,
  type DebugEvent,
  type SessionStats
} from './utils';
import { dispatchDegreeAuditRefresh, dispatchTermsRequired } from '../../utils/appEvents';
import type { SuggestionsPayload } from '../academicPlan/usePlanCore';
import { getErrorMessage } from '../../utils/errorUtils';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Intent data from Agent V4
 */
export interface IntentData {
  type?: string;
  intent_type?: string;
  confidence: number;
  routing?: string;
  entities?: Record<string, unknown>;
  parameters?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Parameters for the useAgentV4Chat hook
 */
export interface UseAgentV4ChatParams {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setSessionStats: React.Dispatch<React.SetStateAction<SessionStats>>;
  academicPlanId: number | string | null;
  isMounted: React.RefObject<boolean>;
  onPlanUpdate?: () => Promise<void>;
  isAdmin?: boolean;
  onClearSuggestionsLocally?: () => void;
  onMergeSuggestionsLocally?: (payload: SuggestionsPayload) => void;
}

/**
 * Return type for the useAgentV4Chat hook
 */
export interface UseAgentV4ChatReturn {
  // State
  pendingActions: ChatAction[];
  isConfirming: boolean;
  isStreaming: boolean;
  lastIntent: IntentData | null;
  lastMetrics: ChatMetrics | null;
  hasPendingActions: boolean;

  // Actions
  sendV4StreamingMessage: (content: string, action?: string) => Promise<void>;
  confirmActions: () => Promise<void>;
  rejectActions: () => Promise<void>;
  cancelStreaming: () => void;
  resetSession: () => Promise<void>;
  clearV4State: () => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format V4 error into user-friendly message.
 */
/** An Error raised from an SSE `error` event, carrying the structured fields it had. */
export interface StreamError extends Error {
  /** ISO timestamp (with offset) at which a usage limit or the monthly budget resets. */
  resetAt?: string;
  /** Machine-readable refusal code from the central Claude gate. */
  code?: string;
}

/** Refusal codes from the backend's central Claude gate (services/claude_gate.py). */
const USAGE_BLOCK_CODES = new Set(['terms_required', 'user_daily', 'user_monthly', 'global_budget']);

/** True when the error is a usage block (not a fault), so "please try again" is wrong. */
export function isUsageBlock(err: StreamError): boolean {
  return !!err.code && USAGE_BLOCK_CODES.has(err.code);
}

function formatResetDate(resetAt: string | undefined): string | null {
  if (!resetAt) return null;
  const d = new Date(resetAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

function formatResetAt(resetAt: string | undefined): string | null {
  if (!resetAt) return null;
  const d = new Date(resetAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function formatV4Error(err: StreamError): string {
  const errMsg = err.message.toLowerCase();
  const resetAt = formatResetAt(err.resetAt);

  // Central Claude gate refusals carry a `code`; switch on it rather than on
  // prose (open-access plan §3.2 / §4.1). Honest copy: what's off, when it's
  // back, and that the rest of the planner still works.
  // The gate fails closed when it cannot read the acceptance, so a student who
  // has already agreed can see this too; the copy covers both cases.
  if (err.code === 'terms_required') {
    return 'Please agree to the Terms of Service and Privacy Policy to use the AI advisor. If you already have, try again in a moment.';
  }
  if (err.code === 'global_budget') {
    const date = formatResetDate(err.resetAt);
    return `The AI advisor has reached its limit for this month and will be back on ${date ?? 'the 1st'}. Your plan, degree audit and course suggestions still work.`;
  }
  if (err.code === 'user_monthly') {
    const date = formatResetDate(err.resetAt);
    return `You've reached your AI advisor limit for this month. It resets on ${date ?? 'the 1st'}. Your plan and degree audit still work.`;
  }
  if (err.code === 'user_daily') {
    return resetAt
      ? `You've reached your AI advisor limit for today. It resets ${resetAt}. Your plan and degree audit still work.`
      : "You've reached your AI advisor limit for today. It resets tomorrow. Your plan and degree audit still work.";
  }

  // Fallback for responses without a code. The backend distinguishes daily
  // from monthly caps ("Daily usage limit exceeded" / "Monthly usage limit
  // exceeded") and sends `reset_at` for both; a monthly refusal told to "try
  // again tomorrow" would be refused again every day of the month.
  if (errMsg.includes('monthly usage limit')) {
    return resetAt
      ? `Monthly usage limit reached. It resets ${resetAt}.`
      : 'Monthly usage limit reached. It resets on the first of next month.';
  }
  if (errMsg.includes('usage limit') || errMsg.includes('limit exceeded')) {
    return resetAt
      ? `Daily usage limit reached. It resets ${resetAt}.`
      : 'Daily usage limit exceeded. Please try again tomorrow.';
  }
  if (errMsg.includes('429') || errMsg.includes('rate limit')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (errMsg.includes('timeout') || errMsg.includes('timed out')) {
    return 'Request timed out. Please try again.';
  }
  if (errMsg.includes('503') || errMsg.includes('overloaded')) {
    return 'Service temporarily unavailable. Please try again in a moment.';
  }
  if (errMsg.includes('401') || errMsg.includes('unauthorized')) {
    return 'Session expired. Please refresh the page and log in again.';
  }
  if (errMsg.includes('403')) {
    return 'Agent V4 is not enabled. Please check your settings.';
  }
  if (errMsg.includes('500') || errMsg.includes('502') || errMsg.includes('504')) {
    return 'Server error. Please try again.';
  }
  if (errMsg.includes('network') || errMsg.includes('failed to fetch')) {
    return 'Network error. Please check your connection.';
  }

  return err.message;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook providing Agent V4 chat functionality.
 */
/**
 * Shown when the server no longer holds the proposal a click referred to.
 *
 * NOT a session or login problem — the previous wording ("Session expired")
 * said it was, sending users to re-authenticate, which could not help. The
 * server answers "No pending actions" when the proposal was already
 * confirmed/rejected (often on another worker, or by a double-click whose
 * second request lost the claim) or when it aged out after 30 minutes. In the
 * common case the action DID apply, so the useful next step is to look at the
 * plan. The genuinely auth-related 401 message lives in formatV4Error.
 */
const PENDING_ACTIONS_GONE_MESSAGE =
  'That request is no longer pending — it may already have been applied. ' +
  'Check your plan, then ask again if needed.';

export const useAgentV4Chat = ({
  messages,
  setMessages,
  setIsLoading,
  setError,
  setSessionStats,
  academicPlanId,
  isMounted,
  onPlanUpdate,
  isAdmin = false,
  onClearSuggestionsLocally,
  onMergeSuggestionsLocally,
}: UseAgentV4ChatParams): UseAgentV4ChatReturn => {
  const [pendingActions, setPendingActions] = useState<ChatAction[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [lastIntent, setLastIntent] = useState<IntentData | null>(null);
  const [lastMetrics, setLastMetrics] = useState<ChatMetrics | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingMessageIdRef = useRef<number | null>(null);

  /**
   * Restore pending actions from backend.
   * This is called when the tab becomes visible to sync state with the server.
   */
  const restorePendingActions = useCallback(async () => {
    if (!academicPlanId) return;

    try {
      const result = await typedApi.chat.getV4PendingActions(Number(academicPlanId));
      const serverPendingActions = result.pending_actions || [];

      // Only update if there are pending actions on the server that we don't have locally
      if (serverPendingActions.length > 0) {
        setPendingActions(serverPendingActions);
      }
    } catch (err) {
      // Silently fail - this is a best-effort sync
      console.debug('Failed to restore pending actions:', getErrorMessage(err));
    }
  }, [academicPlanId]);

  /**
   * Sync pending actions with backend when tab becomes visible.
   * This ensures pending actions are not lost if the user switches tabs.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User returned to the tab - sync pending actions from backend
        restorePendingActions();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [restorePendingActions]);

  /**
   * Restore pending actions on initial mount.
   * This handles the case where the user refreshes the page while there were pending actions.
   */
  useEffect(() => {
    if (academicPlanId && isMounted.current) {
      restorePendingActions();
    }
    // isMounted is a ref that doesn't change, so it doesn't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicPlanId, restorePendingActions]);

  /**
   * Send a message using Agent V4 with streaming.
   */
  const sendV4StreamingMessage = useCallback(async (content: string, action?: string): Promise<void> => {
    if (!content.trim()) return;
    if (!academicPlanId) {
      setError('No academic plan selected');
      return;
    }

    const userMessage = createUserMessage(content);
    const { message: assistantMessage, id: assistantMessageId } = createStreamingAssistantMessage();
    streamingMessageIdRef.current = assistantMessageId;

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);
    setPendingActions([]);
    setLastIntent(null);
    setLastMetrics(null);

    abortControllerRef.current = new AbortController();

    try {
      const conversationHistory = messages.slice(-10).map((msg) => {
        let enrichedContent = msg.content ?? '';

        // Append action execution results so the agent knows what happened
        if (msg.confirmedActions?.executed?.length) {
          const actionSummaries = msg.confirmedActions.executed
            .map((a) => {
              const params = a.parameters || {};
              const code = params.course_code || params.semester || params.program_code || '';
              return code ? `${a.action_type}(${code})` : a.action_type;
            })
            .join(', ');
          enrichedContent += `\n\n[Actions executed: ${actionSummaries}]`;
        }

        // Without this a truncated answer is replayed as a completed turn, so
        // the model treats a half-finished thought as something it stood
        // behind — and may build on advice it never actually gave. The
        // transport carries role and content only, so the marker has to live
        // in the text, same as the action summaries above.
        if (msg.wasCancelled) {
          enrichedContent += '\n\n[This response was interrupted before it finished.]';
        }

        return { role: msg.role, content: enrichedContent };
      })
        // Drop turns that are still empty AFTER enrichment. An assistant
        // message interrupted before its first token carries content: '', and
        // the API rejects an empty assistant turn — until it scrolled out of
        // the window it would break every send. The backend's own history
        // writer has guarded this all along (`if assistant_response:`); the
        // client builder did not.
        //
        // Filtering on the ENRICHED text, not the raw content, is the whole
        // point. An earlier version filtered first and mirrored only the
        // action-summary condition, so an interrupted-before-first-token turn
        // was dropped entirely and the marker above became unreachable for it
        // — the model was never told the turn existed, while the UI was
        // telling the student "ask again to continue". Filtering the result
        // needs no predicate to keep in sync, so a future marker cannot
        // reintroduce that.
        .filter((msg) => msg.content.trim().length > 0);

      const requestBody = {
        message: content,
        academic_plan_id: academicPlanId,
        conversation_history: conversationHistory,
        ...(isAdmin ? { verbose: true } : {}),
        ...(action ? { action } : {})
      };

      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/chat/v4/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let accumulatedContent = '';
      let intentData: IntentData | null = null;
      let actionsData: ChatAction[] = [];
      let metricsData: ChatMetrics | null = null;
      let debugEventsData: DebugEvent[] = [];
      let lineBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        lineBuffer += chunk;
        const lines = lineBuffer.split('\n');
        // Last element may be an incomplete line — keep it in the buffer
        lineBuffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr) as {
              type: string;
              data?: {
                content?: string;
                actions?: ChatAction[];
                requires_confirmation?: boolean;
                message?: string;
                [key: string]: unknown;
              };
            };

            switch (event.type) {
              case 'status': {
                // Progress lines ("Understanding your request...",
                // "Looking up CMSC422…"). Shown in the empty bubble until
                // the first text arrives; a turn can wait 3-20s for it.
                const statusText = event.data?.message;
                if (typeof statusText === 'string' && statusText) {
                  setMessages(prev => prev.map(msg =>
                    msg.id === assistantMessageId
                      ? { ...msg, statusText }
                      : msg
                  ));
                }
                break;
              }

              case 'intent':
                intentData = event.data as IntentData;
                setLastIntent(intentData);
                break;

              case 'text': {
                accumulatedContent += event.data?.content || '';
                const currentContent = accumulatedContent;
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: currentContent }
                    : msg
                ));
                break;
              }

              case 'actions':
                actionsData = event.data?.actions || [];
                if (event.data?.requires_confirmation && actionsData.length > 0) {
                  setPendingActions(actionsData);
                }
                break;

              case 'actions_executed': {
                // Immediate actions were auto-executed by the backend
                const executedActionType = event.data?.action_type as string | undefined;

                if (executedActionType === 'clear_suggestions' && onClearSuggestionsLocally) {
                  onClearSuggestionsLocally();
                } else if (executedActionType === 'generate_suggestions' && event.data?.suggestions_payload && onMergeSuggestionsLocally) {
                  onMergeSuggestionsLocally(event.data.suggestions_payload as SuggestionsPayload);
                } else if (onPlanUpdate) {
                  try { await onPlanUpdate(); } catch { /* non-critical */ }
                }

                if (academicPlanId) {
                  dispatchDegreeAuditRefresh(Number(academicPlanId));
                }
                break;
              }

              case 'metrics':
                metricsData = event.data as ChatMetrics;
                setLastMetrics(metricsData);
                break;

              case 'debug':
                debugEventsData.push(event.data as DebugEvent);
                break;

              case 'done':
                // Stream complete
                break;

              case 'error': {
                const streamErr: StreamError = new Error(event.data?.message || 'Stream error');
                if (typeof event.data?.reset_at === 'string') streamErr.resetAt = event.data.reset_at;
                if (typeof event.data?.code === 'string') streamErr.code = event.data.code;
                throw streamErr;
              }

              default:
                // Unknown event type - ignore
                break;
            }
          } catch (parseError) {
            // Check if this is a JSON parse error (incomplete chunk)
            if (parseError instanceof SyntaxError) {
              continue;
            }
            throw parseError;
          }
        }
      }

      // Note: the in-stream actions_executed handler already awaits
      // onPlanUpdate(), so no post-stream refresh is needed.

      // Finalize the message
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? {
              ...msg,
              content: accumulatedContent,
              isStreaming: false,
              // Cleared, not merely unset. A message can arrive here already
              // carrying wasCancelled: restoring a thread settles anything
              // still flagged streaming, and nothing aborts on a plan-id
              // change — so switching away mid-stream and back settles a
              // message whose stream is still live, and the next chunk
              // resumes writing into it. Spreading ...msg without this left
              // a finished answer permanently labelled "interrupted".
              wasCancelled: false,
              isAgentV4: true,
              intent: intentData,
              proposedActions: actionsData,
              metrics: metricsData,
              requiresConfirmation: actionsData.length > 0,
              debugEvents: debugEventsData.length > 0 ? debugEventsData : undefined
            }
          : msg
      ));

      // Update session stats
      if (isMounted.current) {
        setSessionStats(prev => ({
          ...prev,
          messageCount: prev.messageCount + 1
        }));
      }

    } catch (err) {
      const error = err as Error & { name?: string };
      if (error.name === 'AbortError') {
        setMessages(prev => prev.map(msg =>
          msg.id === assistantMessageId
            ? { ...msg, isStreaming: false, wasCancelled: true }
            : msg
        ));
      } else {
        const errorMessage = formatV4Error(error);
        setError(errorMessage);
        // Re-sync the user so the TermsGate appears if acceptance is missing.
        if ((error as StreamError).code === 'terms_required') {
          dispatchTermsRequired({ surface: 'chat' });
        }
        // A usage block isn't a fault and retrying won't help until the reset,
        // so show the honest message on its own.
        const bubble = isUsageBlock(error)
          ? errorMessage
          : `I encountered an error: ${errorMessage}. Please try again.`;

        setMessages(prev => prev.map(msg =>
          msg.id === assistantMessageId
            ? {
                ...msg,
                content: bubble,
                isStreaming: false,
                isError: true
              }
            : msg
        ));
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      streamingMessageIdRef.current = null;
      abortControllerRef.current = null;
    }
  }, [messages, academicPlanId, isAdmin, isMounted, setMessages, setIsLoading, setError, setSessionStats, onPlanUpdate, onClearSuggestionsLocally, onMergeSuggestionsLocally]);

  /**
   * Confirm pending actions.
   */
  const confirmActions = useCallback(async (): Promise<void> => {
    if (pendingActions.length === 0) {
      return;
    }

    setIsConfirming(true);
    setError(null);

    try {
      const result = await typedApi.chat.confirmV4Actions({
        academic_plan_id: Number(academicPlanId),
        confirm: true
      });

      const typedResult = result as {
        executed?: boolean;
        success?: boolean;
        actions_count?: number;
        error?: string;
      };

      if (typedResult.executed && typedResult.success) {
        // Add confirmation message
        const confirmMessage: ChatMessage = {
          id: Date.now(),
          role: 'assistant',
          content: `I've executed ${typedResult.actions_count} action(s) successfully.`,
          timestamp: new Date().toISOString(),
          isAgentV4: true,
          confirmedActions: {
            executed: pendingActions,
            count: typedResult.actions_count || 0
          }
        };

        setMessages(prev => [...prev, confirmMessage]);

        // Trigger plan refresh
        if (onPlanUpdate) {
          try {
            await onPlanUpdate();
          } catch {
            // Non-critical
          }
        }
      } else if (typedResult.error) {
        setError(typedResult.error);
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      // If server lost pending actions (e.g., after restart), clear the stale confirm panel
      if (errorMsg.includes('No pending actions') || errorMsg.includes('pending actions')) {
        setError(PENDING_ACTIONS_GONE_MESSAGE);
      } else {
        setError(errorMsg);
      }
    } finally {
      setPendingActions([]);
      setIsConfirming(false);
    }
  }, [pendingActions, academicPlanId, onPlanUpdate, setMessages, setError]);

  /**
   * Reject pending actions.
   */
  const rejectActions = useCallback(async (): Promise<void> => {
    if (pendingActions.length === 0) {
      return;
    }

    setIsConfirming(true);

    try {
      await typedApi.chat.confirmV4Actions({
        academic_plan_id: Number(academicPlanId),
        confirm: false
      });

      // Add rejection message
      const rejectMessage: ChatMessage = {
        id: Date.now(),
        role: 'assistant',
        content: 'I\'ve cancelled the proposed actions. Let me know how else I can help.',
        timestamp: new Date().toISOString(),
        isAgentV4: true,
        rejectedActions: pendingActions
      };

      setMessages(prev => [...prev, rejectMessage]);
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      if (errorMsg.includes('No pending actions') || errorMsg.includes('pending actions')) {
        setError(PENDING_ACTIONS_GONE_MESSAGE);
      } else {
        setError(errorMsg);
      }
    } finally {
      setPendingActions([]);
      setIsConfirming(false);
    }
  }, [pendingActions, academicPlanId, setMessages, setError]);

  /**
   * Cancel streaming response.
   */
  const cancelStreaming = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Hang up on unmount.
   *
   * Without this the fetch outlives the component: the backend generates the
   * whole answer and bills the whole turn, while every chunk is dropped on the
   * floor because React discards setState on an unmounted fiber. The zombie
   * closure also fires a plan refetch and an audit-refresh event for a view
   * that is gone. Aborting is understood server-side — the generator detects
   * GeneratorExit, closes the Claude stream and books only partial usage.
   *
   * This is the cheaper of the two behaviours reachable today. The better one
   * is for the answer to SURVIVE the unmount, which needs the chat session
   * hoisted above the router (plan Phase 2); this goes away when that lands.
   */
  useEffect(() => () => abortControllerRef.current?.abort(), []);

  /**
   * Reset V4 session.
   */
  const resetSession = useCallback(async (): Promise<void> => {
    if (!academicPlanId) return;

    try {
      await typedApi.chat.resetV4Session(Number(academicPlanId));
      setPendingActions([]);
      setLastIntent(null);
      setLastMetrics(null);
    } catch (err) {
      console.error('Failed to reset V4 session:', err);
    }
  }, [academicPlanId]);

  /**
   * Clear all V4 state.
   */
  const clearV4State = useCallback((): void => {
    setPendingActions([]);
    setLastIntent(null);
    setLastMetrics(null);
  }, []);

  return {
    // State
    pendingActions,
    isConfirming,
    isStreaming,
    lastIntent,
    lastMetrics,
    hasPendingActions: pendingActions.length > 0,

    // Actions
    sendV4StreamingMessage,
    confirmActions,
    rejectActions,
    cancelStreaming,
    resetSession,
    clearV4State
  };
};

export default useAgentV4Chat;
